import UndoIcon from '@mui/icons-material/Undo';
import { Box, Button, ButtonBase, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { createContext, useContext } from 'react';

import colors from 'common/constants/colors';
import SYSTEM_DEFICIT from 'common/constants/inventoryLabels';
import { commitBeforePrintText } from '../utils/bulkLotNdcHelperText';
import {
  REVIEW_GROUP,
  REVIEW_GROUP_TITLE,
  compareReviewGroupOrder,
} from '../utils/bulkLotNdcReviewGroups';
import { formatQty } from '../utils/bulkLotNdcStatus';
import { warningTextTone } from '../utils/warningTone';

/**
 * POM-6534 — the group rows of the Review Lot & NDC grid.
 *
 * The per-group numbers reach these renderers through React context rather than grid options:
 * AG Grid renders custom components in portals under AgGridReact, so a context value updates them
 * on every render without asking the grid to refresh.
 */
export const ReviewGroupSummaryContext = createContext(null);

const ROW_HEIGHT = 76;
const GROUP_ROW_HEIGHT = 48;
// Pending commit's header holds up to two lines of notes beside its title.
const PENDING_GROUP_ROW_HEIGHT = 64;
const PLACEHOLDER_ROW_HEIGHT = 44;

export const isGroupPlaceholderRow = ({ rowNode }) => !!rowNode.data?.isGroupPlaceholder;

export const reviewRowHeight = ({ node }) => {
  if (node.group) {
    return node.key === REVIEW_GROUP.PENDING_COMMIT ? PENDING_GROUP_ROW_HEIGHT : GROUP_ROW_HEIGHT;
  }

  return node.data?.isGroupPlaceholder ? PLACEHOLDER_ROW_HEIGHT : ROW_HEIGHT;
};

// "No action needed" starts collapsed on every open; nothing in it is waiting on anyone.
export const isReviewGroupOpenByDefault = ({ key }) => key !== REVIEW_GROUP.NO_ACTION_NEEDED;

export const compareReviewGroupNodes = ({ nodeA, nodeB }) =>
  compareReviewGroupOrder(nodeA.key, nodeB.key);

/**
 * The grid's group column: hidden, and out of the Columns rail, because the groups are the
 * screen's structure rather than a view the user arranges.
 */
export const REVIEW_GROUP_COLUMN = {
  field: 'reviewGroup',
  rowGroup: true,
  hide: true,
  suppressColumnsToolPanel: true,
};

export const ROW_CLASS_RULES = { 'review-row-moved': ({ data }) => !!data?.isRecentlyMoved };

/** Group-row tint and the brief highlight on a row that has just changed group. */
export const GROUPED_GRID_SX = {
  '& .ag-row-group': { bgcolor: 'grey.100' },
  '@keyframes reviewRowMoved': {
    '0%, 35%': { backgroundColor: colors.reviewRowMovedHighlight },
    '100%': { backgroundColor: 'transparent' },
  },
  '& .ag-row.review-row-moved .ag-cell': { animation: 'reviewRowMoved 1.8s ease-out' },
  '@media (prefers-reduced-motion: reduce)': {
    '& .ag-row.review-row-moved .ag-cell': {
      animation: 'none',
      backgroundColor: colors.reviewRowMovedHighlight,
    },
  },
};

/**
 * A row just edited lands in Pending commit, which may be collapsed or scrolled away: open it and
 * bring the row into view so its highlight is seen.
 */
export const revealRecentlyMovedRow = ({ api, rowId }) => {
  const node = rowId ? api.getRowNode(rowId) : null;
  if (!node) return;

  if (node.parent?.key === REVIEW_GROUP.PENDING_COMMIT && !node.parent.expanded) {
    node.parent.setExpanded(true);
  }
  api.ensureNodeVisible(node);
};

const formatGroupCount = ({ total, shown }, isFiltered) =>
  isFiltered ? `(${shown} of ${total})` : `(${total})`;

const PendingCommitNotes = ({ changedCount, claimCount, unverifiedQty }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
    <Typography variant='caption' color='text.secondary'>
      {`Not saved yet. ${commitBeforePrintText(changedCount, claimCount)}`}
    </Typography>
    {/* POM-6536: exact, because every lot is weighed against the whole batch (batchLotUsage). */}
    {unverifiedQty > 0 && (
      <Typography variant='caption' sx={{ color: warningTextTone }}>
        {`${formatQty(unverifiedQty)} ${
          unverifiedQty === 1 ? 'unit' : 'units'
        } across this batch will be claimed as `}
        {SYSTEM_DEFICIT.toLowerCase()} — stock dispensed but not yet received into inventory.
      </Typography>
    )}
  </Box>
);

PendingCommitNotes.propTypes = {
  changedCount: PropTypes.number.isRequired,
  claimCount: PropTypes.number,
  unverifiedQty: PropTypes.number.isRequired,
};

PendingCommitNotes.defaultProps = { claimCount: 0 };

/** AG Grid group row inner renderer: the group's title, its count, and what it holds. */
export const ReviewGroupHeader = ({ node }) => {
  const summaries = useContext(ReviewGroupSummaryContext);
  const group = node?.key;
  const summary = summaries?.groups?.[group];
  if (!summary) return REVIEW_GROUP_TITLE[group] ?? null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, lineHeight: 1.3 }}>
      <ButtonBase
        onClick={() => node.setExpanded(!node.expanded)}
        sx={{ fontWeight: 600, fontSize: 14, borderRadius: 1, px: 0.5, flexShrink: 0 }}
      >
        {REVIEW_GROUP_TITLE[group]}{' '}
        <Box component='span' sx={{ ml: 0.5, fontWeight: 400, color: 'text.secondary' }}>
          {formatGroupCount(summary, summaries.isFiltered)}
        </Box>
      </ButtonBase>
      {group === REVIEW_GROUP.PENDING_COMMIT ? (
        <>
          <PendingCommitNotes
            changedCount={summary.total}
            claimCount={summary.claimCount}
            unverifiedQty={summaries.unverifiedQty}
          />
          {/* POM-6537 — puts back every row the last "Also apply" moved. */}
          {summaries.bulkUndo && (
            <Button
              size='small'
              variant='outlined'
              startIcon={<UndoIcon />}
              onClick={(event) => {
                event.stopPropagation();
                summaries.bulkUndo.onUndo();
              }}
              sx={{ ml: 'auto', flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              {`Undo bulk change (${summaries.bulkUndo.count})`}
            </Button>
          )}
        </>
      ) : (
        <Typography variant='caption' color='text.secondary' noWrap>
          {summary.statusCounts}
        </Typography>
      )}
    </Box>
  );
};

ReviewGroupHeader.propTypes = {
  node: PropTypes.shape({
    key: PropTypes.string,
    expanded: PropTypes.bool,
    setExpanded: PropTypes.func,
  }),
};
ReviewGroupHeader.defaultProps = { node: null };

export const GROUP_ROW_RENDERER_PARAMS = { innerRenderer: ReviewGroupHeader, suppressCount: true };

/** Full-width line standing in for the rows of a group that has none on screen. */
export const ReviewGroupPlaceholderRow = ({ data }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', pl: 5.5 }}>
    <Typography variant='body2' color='text.secondary'>
      {data?.placeholderText}
    </Typography>
  </Box>
);

ReviewGroupPlaceholderRow.propTypes = {
  data: PropTypes.shape({ placeholderText: PropTypes.string }),
};
ReviewGroupPlaceholderRow.defaultProps = { data: null };
